import { useCallback, useMemo, createContext, FC } from 'react';
import { ICardSelectorProps, ICardAnalyticItemProps } from './types';
import { CardId } from 'src/presentations/components/organisms/analytics/generalOverview/types';

export interface ICardSelectorContext {
  selectedCards: ICardAnalyticItemProps[];
  setSelectedCards: React.Dispatch<React.SetStateAction<ICardAnalyticItemProps[]>>;
  toggle: (cardId: CardId) => void;
  checkIsActive: (cardId: CardId) => boolean;
  checkIsEnable: (unit: string) => boolean;
  clearSelection: () => void;
}

export const CardSelectorContext = createContext<ICardSelectorContext | undefined>(undefined);

export const CardSelector: FC<ICardSelectorProps> = ({ onCardSelected, selectedCards, setSelectedCards, children }) => {
  const toggle = useCallback(
    (cardId: CardId) => {
      if (onCardSelected) {
        onCardSelected(cardId);
      }
    },
    [onCardSelected]
  );

  const checkIsActive = useCallback((cardId: CardId) => selectedCards.some((c) => c.id === cardId), [selectedCards]);

  const checkIsEnable = useCallback(
    (unit: string) => {
      const isUnitSelected = selectedCards.find((item) => item.unit === unit);
      const totalUnitSelectedCount = new Set(selectedCards.map((item) => item.unit)).size;
      if (totalUnitSelectedCount === 3 && !isUnitSelected) {
        return false;
      }
      return true;
    },
    [selectedCards]
  );

  const clearSelection = useCallback(() => {
    setSelectedCards([]);
  }, [setSelectedCards]);

  const value = useMemo<ICardSelectorContext>(
    () => ({
      selectedCards,
      setSelectedCards,
      toggle,
      checkIsActive,
      checkIsEnable,
      clearSelection,
    }),
    [selectedCards, setSelectedCards, toggle, checkIsActive, checkIsEnable, clearSelection]
  );

  return <CardSelectorContext.Provider value={value}>{children}</CardSelectorContext.Provider>;
};
